import { useState, useEffect } from "react";
import "./Support.css";
import { validEmail } from "../../Constants/RegExp";
import { SupportTicket, GetConfig } from "./Requests";
import { PulseLoader } from "react-spinners";
import Select from "react-select";
import { useTranslation } from "react-i18next";

const Support = () => {
  const { t } = useTranslation("support");

  const errorsObj = {
    subject: "",
    type: "",
    priority: "",
    emailAddress: "",
    phoneNumber: "",
    description: "",
  };

  const [errors, setErrors] = useState(errorsObj);
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("Bug");
  const [priority, setPriority] = useState("Low");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [requestState, setRequestState] = useState(false);
  const [ticketRef, setTicketRef] = useState();

  useEffect(() => {
    GetConfigWrapper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function GetConfigWrapper() {
    GetConfig()
      .then((res) => {
        if (res.status === 200) {
          setEmailAddress(res.data.shopEmail);
          setPhoneNumber(res.data.phoneNumber);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }

  const TYPE_OPTIONS = [
    { value: "Bug", label: t("form.options.type.bug") },
    { value: "Feature Request", label: t("form.options.type.featureRequest") },
    { value: "Feedback", label: t("form.options.type.feedback") },
    { value: "Question", label: t("form.options.type.question") },
    { value: "Other", label: t("form.options.type.other") },
  ];

  const PRIORITY_OPTIONS = [
    { value: "Low", label: t("form.options.priority.low") },
    { value: "Medium", label: t("form.options.priority.medium") },
    { value: "High", label: t("form.options.priority.high") },
    { value: "Critical", label: t("form.options.priority.critical") },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };

    if (subject === "") {
      errorObj.subject = t("errors.subjectRequired");
      error = true;
    }

    if (type === "") {
      errorObj.type = t("errors.typeRequired");
      error = true;
    }

    if (priority === "") {
      errorObj.priority = t("errors.priorityRequired");
      error = true;
    }

    if (phoneNumber === "" || phoneNumber === null) {
      errorObj.phoneNumber = t("errors.phoneRequired");
      // (note: original code didn’t set `error = true` here, preserving behavior)
    }

    if (emailAddress === "") {
      errorObj.emailAddress = t("errors.emailRequired");
      error = true;
    } else if (!validEmail.test(emailAddress)) {
      errorObj.emailAddress = t("errors.invalidEmail");
      error = true;
    }

    if (description === "") {
      errorObj.description = t("errors.descriptionRequired");
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    setIsLoading(true);
    const ticketRefNum = getRndInteger(1000, 900000);
    setTicketRef(ticketRefNum);

    SupportTicket(
      subject,
      type,
      priority,
      emailAddress,
      phoneNumber,
      description,
      ticketRefNum
    )
      .then((res) => {
        if (res.status === 200) setRequestState("success");
      })
      .catch(() => {
        setRequestState("failed");
      })
      .finally(() => setIsLoading(false));
  };

  function getRndInteger(min, max) {
    return Math.floor(Math.random() * (max - min)) + min;
  }

return (
  <div className="sp">
    {isLoading ? (
      <div className="sp-loading">
        <div className="sp-loader" aria-label={t("status.loadingAria")} />
        <p>{t("status.raisingTicket")}</p>
      </div>
    ) : requestState === false ? (
      <div className="sp-card">
        <form className="sp-form" onSubmit={handleSubmit} noValidate>
          {/* Subject */}
          <div className={`sp-field sp-field--full ${errors.subject ? "is-error" : ""}`}>
            <label htmlFor="subject" className="sp-label">
              {t("form.labels.subject")} <span>*</span>
            </label>

            <input
              id="subject"
              name="subject"
              type="text"
              className="sp-input"
              placeholder={t("form.placeholders.subject")}
              onChange={(e) => setSubject(e.target.value)}
              required
            />

            <div className="sp-field__msg">{errors.subject ?? ""}</div>
          </div>

          {/* Type */}
          <div className={`sp-field ${errors.type ? "is-error" : ""}`}>
            <label htmlFor="type" className="sp-label">
              {t("form.labels.type")}
            </label>

            <Select
              inputId="type"
              isSearchable={false}
              placeholder={t("form.placeholders.type")}
              closeMenuOnSelect
              options={TYPE_OPTIONS}
              onChange={(e) => setType(e.value)}
              defaultValue={TYPE_OPTIONS.find((o) => o.value === "Bug")}
              className="sp-select"
              classNamePrefix="rs"
            />

            <div className="sp-field__msg">{errors.type ?? ""}</div>
          </div>

          {/* Priority */}
          <div className={`sp-field ${errors.priority ? "is-error" : ""}`}>
            <label htmlFor="priority" className="sp-label">
              {t("form.labels.priority")}
            </label>

            <Select
              inputId="priority"
              isSearchable={false}
              placeholder={t("form.placeholders.priority")}
              closeMenuOnSelect
              options={PRIORITY_OPTIONS}
              onChange={(e) => setPriority(e.value)}
              defaultValue={PRIORITY_OPTIONS.find((o) => o.value === "Low")}
              className="sp-select"
              classNamePrefix="rs"
            />

            <div className="sp-field__msg">{errors.priority ?? ""}</div>
          </div>

          {/* Email */}
          <div className={`sp-field ${errors.emailAddress ? "is-error" : ""}`}>
            <label htmlFor="email" className="sp-label">
              {t("form.labels.emailAddress")} <span>*</span>
            </label>

            <input
              id="email"
              name="email"
              type="email"
              className="sp-input"
              defaultValue={emailAddress}
              placeholder={t("form.placeholders.email")}
              onChange={(e) => setEmailAddress(e.target.value)}
              required
            />

            <div className="sp-field__msg">{errors.emailAddress ?? ""}</div>
          </div>

          {/* Phone */}
          <div className={`sp-field ${errors.phoneNumber ? "is-error" : ""}`}>
            <label htmlFor="phone" className="sp-label">
              {t("form.labels.phone")} <span>*</span>
            </label>

            <input
              id="phone"
              name="phone"
              type="tel"
              className="sp-input"
              defaultValue={phoneNumber}
              placeholder={t("form.placeholders.phone")}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />

            <div className="sp-field__msg">{errors.phoneNumber ?? ""}</div>
          </div>

          {/* Description */}
          <div className={`sp-field sp-field--full ${errors.description ? "is-error" : ""}`}>
            <label htmlFor="description" className="sp-label">
              {t("form.labels.description")} <span>*</span>
            </label>

            <textarea
              id="description"
              name="description"
              className="sp-textarea"
              rows={7}
              maxLength={2000}
              placeholder={t("form.placeholders.description")}
              onChange={(e) => setDescription(e.target.value)}
              required
            />

            <div className="sp-field__msg">{errors.description ?? ""}</div>
          </div>

          {/* Actions */}
          <div className="sp-actions">
            <button type="submit" className="sp-btn sp-btn--primary">
              {t("form.actions.submitTicket")}
            </button>
          </div>
        </form>
      </div>
    ) : requestState === "success" ? (
      <div className="sp-result sp-result--ok">
        <div className="sp-result__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="54" height="54">
            <path
              d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"
              fill="currentColor"
              opacity=".12"
            />
            <path
              d="M7.5 12.5l3 3 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h2>{t("results.successTitle")}</h2>
        <p>{t("results.successText")}</p>

        <div className="sp-result__ref">
          {t("results.refPrefix")} <strong>#{ticketRef}</strong>
        </div>
      </div>
    ) : (
      <div className="sp-result sp-result--err">
        <div className="sp-result__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="54" height="54">
            <path
              d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"
              fill="currentColor"
              opacity=".12"
            />
            <path
              d="M8 8l8 8M16 8l-8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <h2>{t("results.failedTitle")}</h2>
        <p>{t("results.failedText")}</p>
      </div>
    )}
  </div>
);
};

export default Support;
